import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  captureTrackingParamsFromUrl,
  getTrackingCampaignPayload,
  removeTrackingParamsFromCurrentUrl,
} from './marketingAttribution';

const MAPBOX_SCRIPT_ID = 'mapbox_GL_JS';
const GOOGLE_MAPS_SCRIPT_ID = 'GoogleMapsApi';

/**
 * Include scripts (like Map Provider).
 * These scripts are relevant for whole application: location search in Topbar and maps on different pages.
 * However, if you don't need location search and maps, you can just omit this component from app.js
 * Note: another common point to add <scripts>, <links> and <meta> tags is Page.js
 *       and Stripe script is added in public/index.html
 *
 * Note 2: When adding new external scripts/styles/fonts/etc.,
 *         if a Content Security Policy (CSP) is turned on, the new URLs
 *         should be whitelisted in the policy. Check: server/csp.js
 */
export const IncludeScripts = props => {
  const { marketplaceRootURL: rootURL, maps, analytics } = props?.config || {};
  const {
    googleAnalyticsId,
    plausibleDomains,
    googleAdsConversionSendTo,
    redditPixelId,
  } =
    analytics || {};
  const googleAdsId = googleAdsConversionSendTo?.split('/')[0] || null;

  const { mapProvider, googleMapsAPIKey, mapboxAccessToken } = maps || {};
  const isGoogleMapsInUse = mapProvider === 'googleMaps';
  const isMapboxInUse = mapProvider === 'mapbox';

  // Add Google Analytics script if correct id exists (it should start with 'G-' prefix)
  // See: https://developers.google.com/analytics/devguides/collection/gtagjs
  const hasGoogleAnalyticsv4Id = googleAnalyticsId?.indexOf('G-') === 0;

  // Google Analytics toujours chargé et activé sans condition de consentement.
  useEffect(() => {
    if (typeof window === 'undefined' || !googleAnalyticsId || !hasGoogleAnalyticsv4Id) {
      return;
    }
    captureTrackingParamsFromUrl();
    const campaignPayload = getTrackingCampaignPayload();

    const consentGtagValues = () => ({
      analytics_storage: 'granted',
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
    });

    const loadGoogleTracking = () => {
      window.dataLayer = window.dataLayer || [];
      if (!window.gtag) {
        window.gtag = function gtag() {
          window.dataLayer.push(arguments);
        };
      }

      const existingScript = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');

      if (existingScript) {
        window.gtag('consent', 'update', consentGtagValues());
        window.gtag('event', 'page_view', {
          page_location: window.location.href,
          page_path: window.location.pathname,
          page_title: document.title,
          ...campaignPayload,
        });
        removeTrackingParamsFromCurrentUrl();
        return;
      }

      window.gtag('consent', 'default', {
        ...consentGtagValues(),
      });

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`;
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);

      script.onload = () => {
        window.gtag('js', new Date());
        window.gtag('consent', 'update', consentGtagValues());

        window.gtag('config', googleAnalyticsId, {
          cookie_flags: 'SameSite=None;Secure',
          ...campaignPayload,
        });

        if (googleAdsId?.indexOf('AW-') === 0) {
          window.gtag('config', googleAdsId, {
            cookie_flags: 'SameSite=None;Secure',
          });
        }

        removeTrackingParamsFromCurrentUrl();
      };
    };

    loadGoogleTracking();
  }, [googleAnalyticsId, hasGoogleAnalyticsv4Id, googleAdsId]);

  // Reddit Pixel — same behaviour as the official <head> snippet; runs from the app bundle for CSP.
  useEffect(() => {
    if (typeof window === 'undefined' || !redditPixelId) {
      return;
    }
    const w = window;
    const d = document;
    if (!w.rdt) {
      const p = (w.rdt = function rdtStub() {
        p.sendEvent ? p.sendEvent.apply(p, arguments) : p.callQueue.push(arguments);
      });
      p.callQueue = [];
      const t = d.createElement('script');
      t.src = `https://www.redditstatic.com/ads/pixel.js?pixel_id=${redditPixelId}`;
      t.async = true;
      const s = d.getElementsByTagName('script')[0];
      if (s?.parentNode) {
        s.parentNode.insertBefore(t, s);
      } else {
        d.head.appendChild(t);
      }
    }
    w.rdt('init', redditPixelId);
    w.rdt('track', 'PageVisit');
  }, [redditPixelId]);

  // Collect relevant map libraries
  let mapLibraries = [];
  let analyticsLibraries = [];

  if (isMapboxInUse && mapboxAccessToken) {
    // NOTE: remember to update mapbox-sdk.min.js to a new version regularly.
    // mapbox-sdk.min.js is included from static folder for CSP purposes.
    mapLibraries.push(
      <script key="mapboxSDK" src={`${rootURL}/static/scripts/mapbox/mapbox-sdk.min.js`}></script>
    );
    // License information for v3.7.0 of the mapbox-gl-js library:
    // https://github.com/mapbox/mapbox-gl-js/blob/v3.7.0/LICENSE.txt

    // Add CSS for Mapbox map
    mapLibraries.push(
      <link
        key="mapbox_GL_CSS"
        href="https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.css"
        rel="stylesheet"
        crossOrigin
      />
    );
    // Add Mapbox library
    mapLibraries.push(
      <script
        id={MAPBOX_SCRIPT_ID}
        key="mapbox_GL_JS"
        src="https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.js"
        crossOrigin
      ></script>
    );
  } else if (isGoogleMapsInUse && googleMapsAPIKey) {
    // Add Google Maps library
    mapLibraries.push(
      <script
        id={GOOGLE_MAPS_SCRIPT_ID}
        key="GoogleMapsApi"
        src={`https://maps.googleapis.com/maps/api/js?key=${googleMapsAPIKey}&libraries=places`}
        crossOrigin
      ></script>
    );
  }

  // Plausible Analytics (si configuré)
  if (plausibleDomains) {
    // If plausibleDomains is not an empty string, include their script too.
    analyticsLibraries.push(
      <script
        key="plausible"
        defer
        src="https://plausible.io/js/script.js"
        data-domain={plausibleDomains}
        crossOrigin
      ></script>
    );
  }

  const isBrowser = typeof window !== 'undefined';
  const isMapboxLoaded = isBrowser && window.mapboxgl;

  // If Mapbox is loaded, we can set the accessToken already here.
  // This is the execution flow with the production build,
  // since SSR includes those map libraries to <head> of the app.
  if (isMapboxInUse && isMapboxLoaded && !window.mapboxgl.accessToken) {
    // Add access token for Mapbox library
    window.mapboxgl.accessToken = mapboxAccessToken;
  }

  // If the script is added on client side as a reaction to page navigation or
  // the app is rendered on client side entirely (e.g. HMR/WebpackDevServer),
  // we need to listen when the script is loaded.
  const onMapLibLoaded = () => {
    // At this point we know that map library is loaded after it's dynamically included
    if (isMapboxInUse && !window.mapboxgl.accessToken) {
      // Add access token for Mapbox sdk.
      window.mapboxgl.accessToken = mapboxAccessToken;
    }
  };

  // React Helmet Async doesn't support onLoad prop for scripts.
  // However, it does have onChangeClientState functionality.
  // We can use that to start listen 'load' events when the library is added on client-side.
  const onChangeClientState = (newState, addedTags) => {
    if (addedTags && addedTags.scriptTags) {
      const foundScript = addedTags.scriptTags.find(s =>
        [MAPBOX_SCRIPT_ID, GOOGLE_MAPS_SCRIPT_ID].includes(s.id)
      );
      if (foundScript) {
        foundScript.addEventListener('load', onMapLibLoaded, { once: true });
      }
    }
  };

  const allScripts = [...analyticsLibraries, ...mapLibraries];
  return <Helmet onChangeClientState={onChangeClientState}>{allScripts}</Helmet>;
};
